import React, { useState } from "react";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import { Button, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ReactComponent as AuxiliarySystemIcon } from "../../../assets/HyPET_AuxiliarySystem.svg";
import { ReactComponent as MachineIcon } from "../../../assets/HyPET_MachineIcon.svg";
import { ReactComponent as HydraulicIcon } from "../../assets/Hydraulic.svg";
import { ReactComponent as ChilledWaterIcon } from "../../assets/chilledWater.svg";
import { ReactComponent as PneumaticIcon } from "../../assets/Pneumatic.svg";
import { ReactComponent as OperatorEnabledIcon } from "../../assets/OperatorEnabled.svg";
import { ReactComponent as ElectricalIcon } from "../../assets/Electrical.svg";
import { ReactComponent as HVACIcon } from "../../assets/HVAC.svg";
import { ReactComponent as MotorIcon } from "../../assets/Motor.svg";
import MachineCanvas from "../MachineCanvas";
import ButtonCubeResponse from "../../../Hooks/ButtonCubeResponse";
import ConditionalLink from "../../../utils/conditionalLink";
import { useSelector } from "react-redux";

interface ChildAsset {
  subCategory: any;
  kpiDataPoints: any;
  _id: any;
  asset_name: string;
  assetStatus: number;
  subAssets?: ChildAsset[];
}

interface MachineCardProps {
  machineName?: string;
  childAssets?: ChildAsset[];
  machneIdentity?: string;
  tag?: string;
  assetId?: string;
  pathParts?: string[];
  hypetId?: any;
  hypetChildId?: any;
  hypetSubChildId?: any;
  asset?: any;
  params?: any;
  parentId?: any;
}

interface DataItem {
  subCategory?: any;
  kpiDataPoints?: any;
  _id: any;
  asset_name: string;
  assetStatus?: number;
  subAssets?: ChildAsset[];
}

const transformArrayToObject = (
  arr: DataItem[],
  tagValue: string,
  parentIdValue: string,
  hypetIdValue: string,
  hypetChildIdValue: string,
  hypetSubChildIdValue: string,
  params: any
): Record<string, any> => {
  return arr.reduce((acc, item) => {
    const { asset_name, _id } = item;
    const lowerCaseDescription = asset_name?.toLowerCase();
    acc[lowerCaseDescription] = {
      assetId: _id,
      tag: tagValue,
      parentId: parentIdValue,
      hypetId: hypetIdValue,
      hypetChildId: params.hypet && !params.hypetchild ? _id : hypetChildIdValue,
      hypetSubChildId:
        params.hypet && params.hypetchild && !params.hypetSubChildId
          ? _id
          : hypetSubChildIdValue,
      asset: item,
    };
    return acc;
  }, {} as Record<string, { assetId: string; tag?: string; parentId?: string; hypetId?: string; hypetChildId?: string; hypetSubChildId?: string; params?: any; asset?: any }>);
};

const InjectionMouldingHyPETMachineCard: React.FC<MachineCardProps> = ({
  machineName,
  childAssets,
  machneIdentity,
  tag,
  assetId,
  hypetId,
  hypetChildId,
  hypetSubChildId,
  parentId,
  params,
  pathParts = [],
}) => {
  const [hoveredPart, setHoveredPart] = useState<string>("");
  const { hypet, hypetchild } = params;

   const seedsList = useSelector((state: any) => state.seedsList);
  
    const InjectionAssetSequece = seedsList.seeds.find((obj: any) => {
      return obj.seed_key_name === "InjectionPDMAssetSeq";
    })?.seed_data_value;
  
    let order: string[] = [];
    InjectionAssetSequece?.forEach((obj: any) => {
      if (obj.key === "level3") {
        order = JSON.parse(obj.value);
      }
    });
  

  const navigationState = transformArrayToObject(
    childAssets || [],
    tag || "",
    parentId || "",
    hypetId || "",
    hypetChildId || "",
    hypetSubChildId || "",
    params
  );

  // Function to return the correct icon for each asset
  const renderIcon = (assetName: string) => {
    switch (assetName) {
      case "Auxiliary Systems":
        return <AuxiliarySystemIcon />;
      case "Machines":
        return <MachineIcon />;
      case "Hydraulic":
        return <HydraulicIcon />;
      case "Chilled Water":
        return <ChilledWaterIcon />;
      case "Pneumatic":
        return <PneumaticIcon />;
      case "Operator Enabled":
        return <OperatorEnabledIcon />;
      case "Electrical":
        return <ElectricalIcon />;
      case "HVAC":
        return <HVACIcon />;
      case "Motor":
        return <MotorIcon />;
      default:
        return <></>;
    }
  };

  // Function to generate the correct path based on hierarchy
  const generateNextPath = (item: ChildAsset) => {
    const newPathParts = [...pathParts, item.asset_name]; // Append new asset to the path
    return item.kpiDataPoints || item.subCategory
      ? `/usecase/injection-pdm/hypets/${newPathParts.join("/")}/reportview`
      : `/usecase/injection-pdm/hypets/${newPathParts.join("/")}/machineview`;
  };

  const lookup = (childAssets || []).reduce<Record<string, ChildAsset>>(
    (acc, item) => {
      acc[item.asset_name] = item;
      return acc;
    },
    {}
  );
  const orderedNames = new Set(order);
  const missedItems = (childAssets || []).filter(
    (item) => !orderedNames.has(item.asset_name)
  );
  const arrangedOrder = [
    ...order.map((key) => lookup[key]).filter(Boolean),
    ...missedItems,
  ];

  return (
    <Card>
      {machineName === "Robot" ||
      machineName === "robot" ||
      machineName === "Robots" ||
      machineName === "robots" ||
      machineName === "auxiliary systems" ||
      machineName === "Auxiliary Systems" ? (
        <></>
      ) : (
        <>
          <CardHeader
            action={
              <ButtonCubeResponse
                tag={`${tag}_ProductionState_PV`}
                limit={1}
                statusParam={{ NR: "1", R: "2" }}
              />
            }
            title={machineName}
          />
          <CardContent sx={{ p: 1 }}>
            <CardMedia
              style={{
                backgroundColor: "#232a58",
                minHeight: "250px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <MachineCanvas
                hoveredPart={hoveredPart}
                setHoveredPart={setHoveredPart}
                selectedAsset={arrangedOrder?.[0]?.asset_name}
                machineName={machneIdentity}
                navigationState={navigationState}
              />
            </CardMedia>
          </CardContent>
        </>
      )}

      {/* Render child assets as buttons */}
      <Grid container spacing={1} justifyContent="flex-start" padding={1}>
        {arrangedOrder &&
          arrangedOrder.map((item) => (
            <Grid item key={item.asset_name} xs={12} sm={4} md={3} lg={3}>
              <ConditionalLink
                name={item.asset_name}
                defaultLink={generateNextPath(item)}
                machineLink={`/usecase/injection-pdm/hypets/${pathParts.join(
                  "/"
                )}/${item.asset_name}/reportview`}
                state={{
                  assetId: item._id,
                  tag: tag,
                  parentId: parentId,
                  hypetId: hypetId,
                  hypetChildId: hypet && !hypetchild ? item._id : hypetChildId,
                  hypetSubChildId:
                    hypet && hypetchild && !hypetSubChildId
                      ? item._id
                      : hypetSubChildId,
                  asset: item,
                }}
              >
                <Button
                  sx={{ padding: "20px" }}
                  variant="outlined"
                  size="large"
                  fullWidth
                  style={{
                    backgroundColor: `${
                      hoveredPart === item.asset_name ? "#6A5DFF" : "#232A58"
                    }`,
                    border: "none",
                    justifyContent: "flex-start",
                  }}
                  startIcon={renderIcon(item.asset_name)}
                  onMouseEnter={() => setHoveredPart(item.asset_name)}
                  onMouseLeave={() => setHoveredPart("")}
                >
                  {item.asset_name}
                </Button>
              </ConditionalLink>
            </Grid>
          ))}
      </Grid>
    </Card>
  );
};

export default InjectionMouldingHyPETMachineCard;
